import React, { useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Message,
  Modal,
  RadioGroup,
  Text,
  Textarea,
  toast,
} from '@workwhile/ui'
import { formatCurrency } from 'lib/currency'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { useUserPaymentsContextValue } from '../../context/UserPaymentsProvider'

export const PayoutToAchModal = () => {
  const {
    state: { payoutToAch },
    actions: { closePayoutToAch, submitPayoutToAch },
  } = useUserPaymentsContextValue()

  const [comment, setComment] = useState('')
  const [payoutScope, setPayoutScope] = useState<'full' | 'single'>(
    payoutToAch?.paymentId == null ? 'full' : 'single'
  )

  if (!payoutToAch) {
    return null
  }

  const {
    userId,
    userName,
    paymentId,
    isLoading,
    statusMessage,
    canPayout,
    fullBalance,
    selectedPayment,
    activeBankAccount,
    mutationState,
  } = payoutToAch

  const canSubmit =
    canPayout && comment.trim().length > 0 && !mutationState.isPending

  const handleConfirm = () => {
    submitPayoutToAch(
      {
        userPaymentIds:
          payoutScope === 'single' && paymentId != null
            ? [paymentId]
            : undefined,
        comment: comment.trim(),
      },
      {
        onSuccess: (data) => {
          toast.success(
            `Same-day ACH payout of ${formatCurrency(Number(data.amount))} submitted`,
            { position: 'top-center' }
          )
          closePayoutToAch()
        },
      }
    )
  }

  return (
    <Modal
      open
      onClose={closePayoutToAch}
      position="top"
      title="Manual ACH payout"
      showCancel
      customCta={
        <Flex>
          <Button
            variant="text"
            onClick={closePayoutToAch}
            disabled={mutationState.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canSubmit}
            loading={mutationState.isPending}
          >
            Confirm manual payout
          </Button>
        </Flex>
      }
    >
      <Box mt={3}>
        <Text>
          <Text as="span" fontWeight="bold">
            Worker:
          </Text>{' '}
          {userName} (
          <CopyableLink label={userId} link={`/users/${userId}`} />)
        </Text>
      </Box>

      {isLoading ? <PayoutToAchSkeleton /> : null}

      {statusMessage ? (
        <Message variant="error" mt={3}>
          {statusMessage}
        </Message>
      ) : null}

      {canPayout && paymentId != null ? (
        <Box mt={3}>
          <Field label="Payout amount">
            <RadioGroup
              name="payoutScope"
              value={payoutScope}
              options={[
                {
                  value: 'full',
                  label: `Outstanding balance (${formatCurrency(fullBalance)})`,
                },
                {
                  value: 'single',
                  label: (
                    <Flex alignItems="center" gap={1}>
                      <Text>Single payment</Text>
                      <CopyableLink
                        label={paymentId}
                        link={`/user_payments?payment-id=${paymentId}`}
                      />
                      ({formatCurrency(selectedPayment?.amount ?? 0)})
                    </Flex>
                  ),
                },
              ]}
              onChange={(value) => setPayoutScope(value as 'full' | 'single')}
            />
          </Field>
        </Box>
      ) : null}

      {canPayout && activeBankAccount ? (
        <Box
          mt={3}
          p={3}
          bg="background"
          borderRadius="xxlarge"
          textAlign="center"
        >
          <Text fontSize={1} fontWeight={500}>
            Depositing to
          </Text>
          <Text mt={1} fontSize={2} fontWeight="bold">
            Bank account ending in {activeBankAccount.last4}
          </Text>
        </Box>
      ) : null}
      {canPayout ? (
        <Box mt={3}>
          <Field label="Comment">
            <Textarea
              aria-label="Comment"
              placeholder="Explain why this manual payout is needed"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={3}
              required
              disabled={mutationState.isPending}
            />
          </Field>
        </Box>
      ) : null}

      {canPayout && mutationState.errorMessage ? (
        <Message variant="error" mt={3}>
          {mutationState.errorMessage}
        </Message>
      ) : null}
    </Modal>
  )
}

const PayoutToAchSkeleton = () => (
  <Box mt={3} aria-label="Loading payout details">
    <Box width="35%" height="20px" bg="offWhite" borderRadius="small" />
    <Box mt={2} width="70%" height="20px" bg="offWhite" borderRadius="small" />
    <Box mt={3} height="88px" bg="offWhite" borderRadius="xxlarge" />
    <Box mt={3} height="96px" bg="offWhite" borderRadius="small" />
  </Box>
)
